import React from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import type { TagPair } from "../../../../helpers/cloudPropagationHelpers";
import TagNameDropdown, { type TagNameDropdownProps } from "./TagNameDropdown";

export interface TagValuePairListProps {
  label: string;
  required?: boolean;
  helperText?: string;
  pairs: TagPair[];
  onChange: (pairs: TagPair[]) => void;
  addButtonLabel?: string;
  tagOptions?: TagNameDropdownProps["tagOptions"];
  isLoadingTags?: boolean;
  tagEmptyMessage?: string;
  allowCustomTagValues?: boolean;
}

const TagValuePairList: React.FC<TagValuePairListProps> = ({
  label,
  required = false,
  helperText,
  pairs,
  onChange,
  addButtonLabel = "+ Add TAG:Value pair",
  tagOptions = [],
  isLoadingTags = false,
  tagEmptyMessage = "No tags available",
  allowCustomTagValues = false,
}) => {
  const updatePair = (index: number, field: keyof TagPair, value: string) => {
    onChange(pairs.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const removePair = (index: number) => {
    if (pairs.length <= 1) {
      onChange([{ tagName: "", tagValue: "" }]);
      return;
    }
    onChange(pairs.filter((_, i) => i !== index));
  };

  const addPair = () => {
    onChange([...pairs, { tagName: "", tagValue: "" }]);
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between">
        <div className="text-sm text-semantic-text-primary">
          {label}
          {required && <span className="text-semantic-text-brand ml-0.5">*</span>}
        </div>
        <span className="text-xs text-semantic-text-secondary">AND logic</span>
      </div>
      {helperText && (
        <p className="text-xs text-semantic-text-secondary">{helperText}</p>
      )}
      <div className="flex flex-col gap-2">
        {pairs.map((pair, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <div className="flex justify-center">
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-semantic-bg-brand-subtle text-semantic-text-brand">
                  AND
                </span>
              </div>
            )}
            <div className="flex items-end gap-2 w-full">
              <div className="flex-1 flex items-end gap-2 min-w-0">
                <div className="flex-1 min-w-0">
                  <TagNameDropdown
                    value={pair.tagName}
                    onChange={(v) => updatePair(index, "tagName", v)}
                    tagOptions={tagOptions}
                    isLoadingTags={isLoadingTags}
                    tagEmptyMessage={tagEmptyMessage}
                    allowCustomTagValues={allowCustomTagValues}
                  />
                </div>
                <span className="pb-2 text-semantic-text-secondary flex-shrink-0">:</span>
                <div className="flex-1 min-w-0">
                  <GenericInput
                    value={pair.tagValue}
                    onChange={(v) => updatePair(index, "tagValue", v)}
                    placeholder="Value"
                    className="text-body w-full"
                  />
                </div>
              </div>
              <GenericButton
                label="−"
                customHeight="35px"
                variant="secondary"
                shape="ghost"
                size="sm"
                onClick={() => removePair(index)}
              />
            </div>
          </React.Fragment>
        ))}
      </div>
      <GenericButton
        label={addButtonLabel}
        variant="secondary"
        shape="ghost"
        size="sm"
        className="w-full"
        onClick={addPair}
      />
    </div>
  );
};

export default TagValuePairList;
